/**
 * QuestLabDetailModal.tsx
 *
 * Quest lab result detail view matching legacy quest-lab-details.php.
 * Shows order header/status, documents, test results with visual range bars, order details.
 *
 * The detail content is factored into QuestLabDetailBody so it can render both
 * inline in the patient-detail right sidebar (LabDetailPanel) and inside the
 * full-screen modal shell (QuestLabDetailModal). The modal is a thin wrapper.
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { X, Download, FileText, ChevronDown, ChevronRight, RefreshCw, ClipboardList } from 'lucide-react';
import { getLabDetail, downloadLabReport, resubmitQuestOrder, markQuestAoeViewed } from '@/services/labs.service';
import { downloadLabResultFile } from '@/services/lab-orders.service';
import { getErrorMessage } from '@/services/apiClient';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { VisualRangeBar, FLAG_STYLES } from './VisualRangeBar';

interface AoeUrl {
  label: string;
  url: string;
}

const STATUS_COLORS: Record<string, { bg: string; label: string }> = {
  HD: { bg: '#e48b00', label: 'On Hold' },
  SC: { bg: '#51a1fb', label: 'In Process' },
  IP: { bg: '#51a1fb', label: 'In Process' },
  RP: { bg: '#e48b00', label: 'Order Replaced' },
  DC: { bg: '#e48b00', label: 'Discontinued' },
  CM: { bg: '#83dba2', label: 'Complete' },
  A:  { bg: '#83dba2', label: 'Some Results Available' },
  ER: { bg: '#ff9797', label: 'Order Not Found' },
  CA: { bg: '#ff9797', label: 'Cancelled' },
};

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

interface QuestLabDetailBodyProps {
  labResultId: number;
  /**
   * Pass when the row in the Quest list has no labs_quest_results record yet
   * (i.e. labResultId === 0). The modal fetches via source='quest-order' and
   * the backend resolves it from labs_quest_orders / _ack / _obr / _files —
   * matching the legacy quest-lab-details.php behavior when called with just
   * an orderID.
   */
  orderId?: number;
}

/**
 * The Quest detail content (status header + documents + pending/resubmit +
 * tests + details). Self-contained: fetches its own data and manages the AOE
 * questionnaire / resubmit flow. Renders as a full-height flex column so the
 * parent (modal card or sidebar panel) just needs to give it a height.
 */
export function QuestLabDetailBody({ labResultId, orderId }: QuestLabDetailBodyProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['tests', 'documents', 'pending']));
  const [aoeUrl, setAoeUrl] = useState<string | null>(null);
  const [resubmitMessage, setResubmitMessage] = useState<string | null>(null);
  const [resubmitError, setResubmitError] = useState<string | null>(null);
  // Legacy parity (loadAoeUrl/lab_quest_aoeviewed.php): the resubmit button is
  // revealed only after the AOE questionnaire has been viewed. Seeded from the
  // server's persisted aoeLoaded and flipped true when the iframe loads.
  const [aoeViewedLocal, setAoeViewedLocal] = useState(false);

  const useOrderFetch = labResultId === 0 && typeof orderId === 'number' && orderId > 0;
  const fetchSource = useOrderFetch ? 'quest-order' : 'quest';
  const fetchId = useOrderFetch ? (orderId as number) : labResultId;

  const { data: detail, isLoading, isError, refetch } = useQuery({
    queryKey: ['lab-detail', fetchSource, fetchId],
    queryFn: () => getLabDetail(fetchSource, fetchId),
    enabled: fetchId > 0,
    staleTime: 5 * 60 * 1000,
  });

  const summary = (detail?.summary ?? {}) as Record<string, unknown>;
  const aoeUrls: AoeUrl[] = Array.isArray(summary.aoeUrls) ? (summary.aoeUrls as AoeUrl[]) : [];
  // Backend is authoritative: canResubmit is already gated on AOE presence and
  // the absence of a requisition (REQ), so the section disappears once the
  // order is accepted — matching legacy quest-lab-details.php.
  const canResubmit = Boolean(summary.canResubmit);
  const aoeViewed = aoeViewedLocal || Boolean(summary.aoeLoaded);
  const orderNumber = detail?.orderNumber ? Number(detail.orderNumber) : 0;
  const patientId = detail?.patientId ?? 0;

  function openAoe(url: string) {
    setAoeUrl(url);
  }

  function handleAoeIframeLoaded() {
    setAoeViewedLocal(true);
    if (orderNumber > 0) {
      void markQuestAoeViewed(orderNumber);
    }
  }

  const resubmitMutation = useMutation({
    mutationFn: () => resubmitQuestOrder(orderNumber, patientId),
    onSuccess: (result) => {
      setResubmitError(null);
      // The legacy resubmit core returns an HTML status snippet; strip tags for display.
      const text = String(result.response ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      setResubmitMessage(text || 'Resubmission processed.');
      void refetch();
    },
    onError: (err: unknown) => {
      setResubmitMessage(null);
      setResubmitError(getErrorMessage(err, 'Failed to resubmit the order.'));
    },
  });

  // Close the AOE overlay on Escape (the overlay is the only local layer this
  // body owns; parent shells handle Escape for the modal/panel itself).
  useEffect(() => {
    if (!aoeUrl) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setAoeUrl(null); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [aoeUrl]);

  function toggleSection(key: string) {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  const statusInfo = detail?.status ? (STATUS_COLORS[detail.status] ?? { bg: '#ccc', label: detail.status }) : null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Status header */}
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-6 py-3 flex-shrink-0">
        <h2 className="text-base font-semibold text-slate-900">
          Order #{detail?.orderNumber ?? (labResultId || orderId)}
          {detail?.patientName ? ` — ${detail.patientName}` : ''}
          {detail?.patientDisplayId ? ` (#${detail.patientDisplayId})` : ''}
        </h2>
        {statusInfo && (
          <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold" style={{ backgroundColor: statusInfo.bg, color: '#fff' }}>
            {statusInfo.label}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
        {isLoading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : isError || !detail ? (
          <p className="text-sm text-red-600">Failed to load lab details.</p>
        ) : (
          <>
            {/* Documents */}
            {detail.files.length > 0 && (
              <div>
                <button type="button" onClick={() => toggleSection('documents')} className="flex items-center gap-2 w-full text-left py-2">
                  {expandedSections.has('documents') ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Documents</h3>
                </button>
                {expandedSections.has('documents') && (
                  <div className="space-y-1 ml-6">
                    {detail.files.map((file, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => {
                          // Prefer the URL the backend embeds per row so
                          // REQ/ABN docs, embedded PDF reports, and the
                          // account-scoped specimen-label templates all
                          // route to the correct endpoint. Fall back to the
                          // embedded-PDF report only when no per-file URL
                          // is present (older rows pre-label-parity work).
                          if (file.url) {
                            void downloadLabResultFile(file.url).then((result) => {
                              if (!result) return;
                              const objectUrl = URL.createObjectURL(result.blob);
                              const a = document.createElement('a');
                              a.href = objectUrl;
                              a.download = result.filename || file.name || 'lab_file';
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              URL.revokeObjectURL(objectUrl);
                            });
                            return;
                          }
                          // Fallback only makes sense for a real result row.
                          // Order-only views (pending orders) have
                          // labResultId === 0, and the result-keyed report
                          // endpoint would 404 with "Quest lab result not
                          // found"; skip it rather than dead-end.
                          if (labResultId > 0) {
                            void downloadLabReport('quest', labResultId);
                          }
                        }}
                        className="flex items-center gap-2 w-full text-left rounded-md border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50"
                      >
                        <FileText className="h-4 w-4 text-red-500 shrink-0" />
                        <span className="text-slate-800">{file.label || file.name}</span>
                        <Download className="h-3.5 w-3.5 text-slate-400 ml-auto" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Pending actions: AOE questionnaires + resubmit */}
            {canResubmit && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <button type="button" onClick={() => toggleSection('pending')} className="flex items-center gap-2 w-full text-left">
                  {expandedSections.has('pending') ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <h3 className="text-sm font-semibold text-amber-800 uppercase tracking-wide">Pending — Action Required</h3>
                </button>
                {expandedSections.has('pending') && (
                  <div className="mt-3 ml-6 space-y-3">
                    <p className="text-sm text-amber-800">
                      This order is pending. Provide any required test information (AOE) below, then resubmit to Quest Diagnostics.
                    </p>
                    {aoeUrls.length > 0 && (
                      <div className="space-y-1">
                        {aoeUrls.map((aoe, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => openAoe(aoe.url)}
                            className="flex items-center gap-2 w-full text-left rounded-md border border-amber-300 bg-white px-3 py-2 text-sm hover:bg-amber-100"
                          >
                            <ClipboardList className="h-4 w-4 text-amber-600 shrink-0" />
                            <span className="text-amber-900">{aoe.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {resubmitMessage && (
                      <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">{resubmitMessage}</div>
                    )}
                    {resubmitError && (
                      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{resubmitError}</div>
                    )}
                    {aoeViewed ? (
                      <Button
                        size="sm"
                        onClick={() => resubmitMutation.mutate()}
                        loading={resubmitMutation.isPending}
                        disabled={orderNumber <= 0 || patientId <= 0}
                      >
                        <RefreshCw className="h-3.5 w-3.5 mr-1" />
                        Resubmit Order
                      </Button>
                    ) : (
                      <p className="text-xs italic text-amber-700">
                        Open the AOE questionnaire above to enable resubmission.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Tests / Results */}
            <div>
              <button type="button" onClick={() => toggleSection('tests')} className="flex items-center gap-2 w-full text-left py-2">
                {expandedSections.has('tests') ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Tests</h3>
              </button>
              {expandedSections.has('tests') && (
                <div className="space-y-4 ml-2">
                  {detail.sections.length === 0 && detail.items.length === 0 ? (
                    <p className="text-sm text-slate-400 italic">No test results available.</p>
                  ) : (
                    detail.sections.map((section, si) => (
                      <div key={si} className="rounded-lg border border-slate-200 overflow-hidden">
                        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center justify-between">
                          <span className="text-sm font-semibold text-blue-900">{section.title}</span>
                          {section.subtitle && <span className="text-xs text-blue-600">{section.subtitle}</span>}
                        </div>
                        {'observations' in section && Array.isArray((section as any).observations) && (section as any).observations.length > 0 ? (
                          <table className="min-w-full divide-y divide-slate-100 text-sm">
                            <thead className="bg-slate-50">
                              <tr className="text-left text-[11px] font-medium uppercase tracking-wide text-slate-500">
                                <th className="px-4 py-2">Test</th>
                                <th className="px-4 py-2">Obv. Code</th>
                                <th className="px-4 py-2">Result</th>
                                <th className="px-4 py-2">Ref. Range</th>
                                <th className="px-4 py-2">Unit</th>
                                <th className="px-4 py-2">Flags</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {((section as any).observations as any[]).map((obs: any, oi: number) => {
                                const flag = obs.flag ?? '';
                                const flagStyle = FLAG_STYLES[flag] ?? { bg: '#ffffff', text: '#000000', label: flag };
                                const isDNR = obs.value === 'DNR';
                                const isPEND = obs.value === 'PEND';

                                return (
                                  <tr
                                    key={oi}
                                    style={{
                                      backgroundColor: isDNR ? '#f5f5f5' : isPEND ? '#e3e3e3' : flagStyle.bg,
                                      color: isDNR || isPEND ? '#666' : flagStyle.text,
                                      display: isDNR ? 'none' : undefined,
                                    }}
                                  >
                                    <td className="px-4 py-2 font-medium">{obs.label ?? obs.code}</td>
                                    <td className="px-4 py-2 text-slate-500 text-xs font-mono">{obs.code}</td>
                                    <td className="px-4 py-2">
                                      <span className="font-semibold">{obs.value}</span>
                                      {obs.referenceRange && obs.value && (
                                        <VisualRangeBar value={obs.value} referenceRange={obs.referenceRange} abnormalFlag={flag} />
                                      )}
                                    </td>
                                    <td className="px-4 py-2 text-slate-500">{obs.referenceRange ?? '—'}</td>
                                    <td className="px-4 py-2 text-slate-500">{obs.unit ?? '—'}</td>
                                    <td className="px-4 py-2">
                                      {flag && flag !== 'N' ? (
                                        <span className="text-xs font-semibold">{flagStyle.label}</span>
                                      ) : '—'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        ) : (
                          <p className="px-4 py-3 text-sm text-slate-400 italic">No results for this test.</p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Order Details */}
            {detail.summary && (
              <div>
                <button type="button" onClick={() => toggleSection('details')} className="flex items-center gap-2 w-full text-left py-2">
                  {expandedSections.has('details') ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Details</h3>
                </button>
                {expandedSections.has('details') && (
                  <div className="ml-6 rounded-lg border border-slate-200 p-4 grid grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                      <h4 className="font-semibold text-slate-700">Patient</h4>
                      <p><span className="text-slate-500">Name:</span> {detail.patientName ?? '—'}</p>
                      <p><span className="text-slate-500">DOB:</span> {detail.birthDate ?? '—'}</p>
                      <p><span className="text-slate-500">ID:</span> {detail.patientDisplayId ?? '—'}</p>
                      {detail.summary.controlId ? <p><span className="text-slate-500">Control ID:</span> {String(detail.summary.controlId)}</p> : null}
                      {detail.summary.requestId ? <p><span className="text-slate-500">Request ID:</span> {String(detail.summary.requestId)}</p> : null}
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-semibold text-slate-700">Order</h4>
                      <p><span className="text-slate-500">Order #:</span> {detail.orderNumber ?? '—'}</p>
                      <p><span className="text-slate-500">Ordered:</span> {formatDate(detail.orderedAt)}</p>
                      <p><span className="text-slate-500">Status:</span> {statusInfo?.label ?? detail.status ?? '—'}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* AOE questionnaire iframe overlay — fixed so it works from the sidebar
          panel or the modal shell. */}
      {aoeUrl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-black/50" onClick={() => setAoeUrl(null)} />
          <div className="relative z-10 w-full max-w-3xl h-[80vh] flex flex-col rounded-lg bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2 bg-slate-50">
              <h3 className="text-sm font-semibold text-slate-800">Required Test Information (AOE)</h3>
              <button type="button" onClick={() => setAoeUrl(null)} className="rounded p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <iframe title="Quest AOE questionnaire" src={aoeUrl} onLoad={handleAoeIframeLoaded} className="flex-1 w-full" />
            <div className="border-t border-slate-200 px-4 py-2 flex justify-end">
              <Button size="sm" onClick={() => { setAoeUrl(null); resubmitMutation.mutate(); }} loading={resubmitMutation.isPending}>
                <RefreshCw className="h-3.5 w-3.5 mr-1" />
                Done — Resubmit
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface QuestLabDetailModalProps {
  open: boolean;
  onClose: () => void;
  labResultId: number;
  orderId?: number;
}

export function QuestLabDetailModal({ open, onClose, labResultId, orderId }: QuestLabDetailModalProps) {
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', handler); };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-8 pb-8">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-4xl max-h-[90vh] flex flex-col rounded-lg bg-white shadow-xl overflow-hidden">
        <div className="flex items-center justify-end border-b border-slate-200 px-3 py-2 flex-shrink-0">
          <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 min-h-0">
          <QuestLabDetailBody labResultId={labResultId} orderId={orderId} />
        </div>
      </div>
    </div>
  );
}
